/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useEffect, useState } from 'react';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import root from 'window-or-global';
import { useAnalytics } from '@/utils/analytics/useAnalytics';
import { usePageViewCall } from '@/utils/analytics/usePageViewCall';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { analyticsPageType } from '@/constants/analytics/analyticsPageType';
import { siteSections } from '@/utils/siteSections';
import { useLocationsParams } from './useLocationsParams';

export const useLocationsAnalytics = () => {
  const hasSetupAnalytics = useAnalytics();
  const pageViewCall = usePageViewCall();
  const router = useLegacyRouter();
  const { data: storeDetailsData } = useStoreDetailsData();
  const { locationsHome } = useLocationsParams();
  const [pageViewCalled, setPageViewCalled] = useState(false);

  useEffect(() => {
    setPageViewCalled(false);
  }, [router.asPath]);

  useEffect(() => {
    if (!pageViewCalled && hasSetupAnalytics) {
      const pathname = root.location.pathname;

      const escapeRegExp = (string: string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const escapedLocationsHome = escapeRegExp(locationsHome);
      const prefixPattern = new RegExp(`^/${escapedLocationsHome}/?`);
      const normalizedPath = pathname.replace(prefixPattern, '/');

      let pageNamePath = normalizedPath.replace(/\//g, ':');

      pageNamePath = pageNamePath.replace(/^:+|:+$/g, '');

      const pageName = pageNamePath ? `az:locations:${pageNamePath}` : 'az:locations';
      const seoPageType = `LOC:${pathname}`;

      const currentUrl = root.location.href;
      const documentReferrer = root.document.referrer;
      const isExternalTraffic =
        currentUrl.includes('cmpid') ||
        (!!documentReferrer && !documentReferrer.includes('autozone.com'));

      const preferredStoreId = storeDetailsData?.storeNumber;

      const pageSpecificAnalyticsData = {
        pageName,
        pageType: analyticsPageType.locations,
        siteSection: siteSections.siteSectionLocations,
        pageURL: currentUrl,
        preferredStoreId,
        seoPageType,
        ...(isExternalTraffic && { seoEntryPageType: seoPageType }),
      };

      pageViewCall(pageSpecificAnalyticsData);
      setPageViewCalled(true);
    }
  }, [
    hasSetupAnalytics,
    pageViewCall,
    pageViewCalled,
    router.asPath,
    storeDetailsData?.storeNumber,
    locationsHome,
  ]);
};
